import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Link, useLocation } from "react-router-dom";
import { Grower } from "../models/grower";
import {
  apiProvider,
  STOCK_CHANGED_EVENT,
} from "../services/api/utilities/provider";

interface LowStockItem {
  id: number;
  lotId: number;
  lotNumber: string;
  kind: "seed" | "packaging";
  name: string;
  detail: string;
  quantity: number;
  threshold: number;
  unit: string;
}

const LowStockContext = createContext<{
  items: LowStockItem[];
  unit: "kg" | "lb";
  showAlerts: () => void;
}>({ items: [], unit: "kg", showAlerts: () => {} });
const itemKey = (item: LowStockItem) => `${item.kind}:${item.lotId}`;
const itemURL = (item: LowStockItem) =>
  `/home/${item.kind === "seed" ? "seeds" : "pkgs"}/${item.id}`;
const stockText = (item: LowStockItem, unit: "kg" | "lb") => {
  const factor = item.kind === "seed" && unit === "lb" ? 1 / 0.45359237 : 1;
  const label = item.kind === "seed" ? unit : "packages";
  const format = (value: number) =>
    (value * factor).toLocaleString(undefined, { maximumFractionDigits: 3 });
  return `${format(item.quantity)} ${label} remaining · alert at ${format(item.threshold)} ${label} or less`;
};

export function LowStockProvider({
  grower,
  children,
}: {
  grower: Grower;
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<LowStockItem[]>([]);
  const [failed, setFailed] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [open, setOpen] = useState(false);
  const dismissed = useRef(new Set<string>());
  const location = useLocation();
  const enabled = grower.Plan === "pro";
  const unit = grower.UnitSystem === "imperial" ? "lb" : "kg";
  const storageKey = `seedleaf:low-stock-dismissed:${grower.id}`;
  const day = new Date().toLocaleDateString("en-CA");

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) ?? "null");
      dismissed.current = new Set(
        saved?.day === day && Array.isArray(saved?.items) ? saved.items : [],
      );
    } catch {
      dismissed.current = new Set();
    }
    setItems([]);
    setOpen(false);
  }, [storageKey, day]);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setOpen(false);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setFailed(false);

    apiProvider
      .getAll("low-stock")
      .then((response: any) => {
        if (cancelled) return;
        const next: LowStockItem[] = response.data ?? [];
        setItems(next);
        setFailed(false);
        setOpen(
          (alreadyOpen) =>
            next.length > 0 &&
            (alreadyOpen ||
              next.some((item) => !dismissed.current.has(itemKey(item)))),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setOpen(false);
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, grower.id, location.pathname, refresh]);

  useEffect(() => {
    if (!enabled) return;
    let pending: ReturnType<typeof setTimeout>;
    const schedule = () => {
      clearTimeout(pending);
      pending = setTimeout(() => setRefresh((value) => value + 1), 500);
    };
    window.addEventListener(STOCK_CHANGED_EVENT, schedule);
    window.addEventListener("focus", schedule);
    const interval = setInterval(schedule, 60000);
    return () => {
      clearTimeout(pending);
      clearInterval(interval);
      window.removeEventListener(STOCK_CHANGED_EVENT, schedule);
      window.removeEventListener("focus", schedule);
    };
  }, [enabled]);

  const dismiss = () => {
    items.forEach((item) => dismissed.current.add(itemKey(item)));
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ day, items: Array.from(dismissed.current) }),
      );
    } catch {
      /* Keep the in-memory dismissal when storage is unavailable. */
    }
    setOpen(false);
  };

  return (
    <LowStockContext.Provider
      value={{
        items: enabled ? items : [],
        unit,
        showAlerts: () => setOpen(true),
      }}
    >
      {children}
      {enabled && failed && (
        <Alert
          severity="warning"
          sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1400 }}
          action={
            <Button onClick={() => setRefresh((value) => value + 1)}>
              Retry
            </Button>
          }
        >
          Unable to check low stock.
        </Alert>
      )}
      <Dialog
        open={enabled && open}
        onClose={dismiss}
        fullWidth
        maxWidth="sm"
        aria-labelledby="low-stock-title"
      >
        <DialogTitle id="low-stock-title">
          Low stock: {items.length} {items.length === 1 ? "lot" : "lots"}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            These lots are at or below their stock limits. Dismissing this
            message keeps the warnings visible in the app.
          </Alert>
          <List>
            {items.map((item) => (
              <ListItem
                key={itemKey(item)}
                component={Link}
                to={itemURL(item)}
                onClick={dismiss}
              >
                <ListItemText
                  primary={`${item.name}${item.detail ? ` — ${item.detail}` : ""} · Lot ${item.lotNumber}`}
                  secondary={stockText(item, unit)}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={dismiss}>
            Dismiss for today
          </Button>
        </DialogActions>
      </Dialog>
    </LowStockContext.Provider>
  );
}

export function LowStockSummary() {
  const { items, showAlerts } = useContext(LowStockContext);
  if (!items.length) return null;
  return (
    <Tooltip title="Review low-stock lots" describeChild>
      <Chip
        onClick={showAlerts}
        clickable
        color="warning"
        icon={<WarningAmberIcon />}
        label={`Low stock: ${items.length}`}
        sx={{ mr: 1 }}
      />
    </Tooltip>
  );
}

export function LowStockIndicator({
  kind,
  id,
  lotId,
}: {
  kind: LowStockItem["kind"];
  id?: number;
  lotId?: number;
}) {
  const { items, unit } = useContext(LowStockContext);
  const matching = items.filter(
    (value) =>
      value.kind === kind &&
      (lotId != null ? value.lotId === lotId : value.id === id),
  );
  const item = matching[0];
  if (!item) return null;
  return (
    <Tooltip
      title={matching
        .map((lot) => `Lot ${lot.lotNumber}: ${stockText(lot, unit)}`)
        .join("; ")}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          ml: 1,
          verticalAlign: "middle",
          color: "warning.main",
        }}
      >
        <WarningAmberIcon
          fontSize="small"
          titleAccess={`Low stock: ${stockText(item, unit)}`}
        />
      </Box>
    </Tooltip>
  );
}
